import { type ReactNode, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useHydrated } from "remix-utils/use-hydrated";

import { partsOf } from "~/hec/utils/utc-date";
import { cn } from "~/lib/ui";

import { countdownTo, deadlineEnd, pickDeadline } from "./deadline";

/**
 * FSA deadline ticker (COM-471..476), per the live Fastr spender page (production;
 * devhec copy is identical). Fallback/passed copy await sign-off. The pill hides
 * until hydration (CLIENT clock and zone).
 */
const messages = defineMessages({
  headline: {
    id: "fsaDeadlineTicker.headline",
    defaultMessage:
      "Have {month, select, 8 {an} 11 {an} other {a}} {date} deadline?",
  },
  fallbackHeadline: {
    id: "fsaDeadlineTicker.fallback_headline",
    defaultMessage: "Have an FSA deadline?",
  },
  confirm: {
    id: "fsaDeadlineTicker.confirm",
    defaultMessage:
      "Contact your administrator or HR to confirm your FSA deadline.",
  },
  passed: {
    id: "fsaDeadlineTicker.passed",
    defaultMessage: "Your {date} FSA deadline has passed.",
  },
  timerLabel: {
    id: "fsaDeadlineTicker.timer_label",
    defaultMessage:
      "{days, plural, one {# day} other {# days}}, {hours, plural, one {# hour} other {# hours}}, {minutes, plural, one {# minute} other {# minutes}}, {seconds, plural, one {# second} other {# seconds}}",
  },
  days: { id: "fsaDeadlineTicker.unit_days", defaultMessage: "D" },
  hours: { id: "fsaDeadlineTicker.unit_hours", defaultMessage: "H" },
  minutes: { id: "fsaDeadlineTicker.unit_minutes", defaultMessage: "M" },
  seconds: { id: "fsaDeadlineTicker.unit_seconds", defaultMessage: "S" },
});

const UNITS = ["days", "hours", "minutes", "seconds"] as const;

export function FsaDeadlineTicker({
  deadlines,
  asOf,
  className,
}: {
  deadlines: readonly string[];
  /** The loader's render time: server and hydration pick at it (UTC day-end), so they match. */
  asOf?: number;
  className?: string;
}) {
  const intl = useIntl();
  const hydrated = useHydrated();
  const [now, setNow] = useState(() => Date.now());

  const dated = deadlines.some(date => deadlineEnd(date) !== null);
  const placeholder = !hydrated && dated;
  // Hydrated: the shopper's clock and local day. The digits wait for it.
  const t = hydrated ? now : (asOf ?? now);
  const picked = pickDeadline(deadlines, t, !hydrated);
  const left = picked === null ? null : countdownTo(picked.end, t);
  // Counting down: wake just after each second boundary. Passed: never.
  const wakeAt = left && picked ? picked.end : null;
  useEffect(() => {
    if (wakeAt === null) return;
    let id: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      const t = Date.now();
      setNow(t);
      if (t >= wakeAt) return;
      id = setTimeout(tick, (wakeAt - t) % 1000 || 1000);
    };
    tick();
    return () => clearTimeout(id);
  }, [wakeAt]);

  // The date as authored, not as an instant: immune to any provider timeZone.
  const date =
    picked === null
      ? ""
      : intl.formatDate(`${picked.date}T00:00:00Z`, {
          month: "numeric",
          day: "numeric",
          timeZone: "UTC",
        });
  // Picks the headline's article: "an 8/31", "an 11/30", "a 9/30".
  const month = picked === null ? "" : String(partsOf(picked.date).month + 1);

  let headline = intl.formatMessage(messages.fallbackHeadline);
  let body: ReactNode = null;
  if (picked !== null) {
    headline = intl.formatMessage(messages.headline, { month, date });
    body = left ? (
      <div
        role="timer"
        data-testid="fsa-deadline-ticker-countdown-row"
        aria-label={
          placeholder
            ? undefined
            : intl.formatMessage(messages.timerLabel, { ...left })
        }
        aria-hidden={placeholder || undefined}
        className={cn(
          "flex items-baseline justify-center gap-3 font-medium tabular-nums text-foreground",
          placeholder && "invisible",
        )}
      >
        {UNITS.map(unit => (
          <span key={unit}>
            {/* A fresh node when the value crosses 10 to 9: Chrome counts a moved text node as a layout shift. */}
            <span
              key={placeholder ? "empty" : String(left[unit] < 10)}
              data-testid={`fsa-deadline-ticker-${unit}-count`}
              className="text-xl md:text-2xl"
            >
              {placeholder ? "" : left[unit]}
            </span>
            <span className="ml-0.5 text-3xs font-light text-gray-03">
              {intl.formatMessage(messages[unit])}
            </span>
          </span>
        ))}
      </div>
    ) : (
      <p
        data-testid="fsa-deadline-ticker-passed-message"
        aria-hidden={placeholder || undefined}
        className={cn(
          "text-sm font-medium text-foreground md:text-xl",
          placeholder && "invisible",
        )}
      >
        {intl.formatMessage(messages.passed, { date })}
      </p>
    );
  }

  return (
    <section
      data-testid="fsa-deadline-ticker-section"
      className={cn(
        "bg-linear-to-r/srgb from-4% via-83% to-125% relative mx-auto mt-3 w-full max-w-[54.5rem] rounded-md from-blue-02 via-indigo-04 to-pink-01 p-px",
        className,
      )}
    >
      <p
        data-testid="fsa-deadline-ticker-headline-message"
        aria-hidden={placeholder || undefined}
        className={cn(
          "bg-linear-to-r/srgb from-4% via-83% to-125% absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full from-blue-02 via-indigo-04 to-pink-01 px-4 py-0.5 text-sm font-medium uppercase text-white md:text-base",
          placeholder && "invisible",
        )}
      >
        {headline}
      </p>
      <div className="rounded-[calc(0.375rem-1px)] bg-background px-4 pb-2 pt-4 text-center">
        {dated && (
          <div className="flex items-center justify-center md:text-xl">
            {body}
          </div>
        )}
        <p
          data-testid="fsa-deadline-ticker-confirm-message"
          className="text-xs font-light text-gray-03"
        >
          {intl.formatMessage(messages.confirm)}
        </p>
      </div>
    </section>
  );
}
